import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CheckCircle2 } from "lucide-react";
import { api, errMsg, fmtDate, fmtEuro } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AppointmentDialog } from "@/components/AppointmentDialog";
import { Panel, GoldButton, GhostButton, Empty, Field, Select, StatusBadge, ProductImage, inputCls } from "@/components/common";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const useList = (url) => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(url).then((r) => setItems(r.data)), [url]);
  useEffect(() => { load(); }, [load]);
  return [items, load];
};

const blankT = { treatment_id: "", treatment_name: "", operator_id: "", date: "", status: "da_effettuare", notes: "" };

const TreatmentDialog = ({ state, setState, onSave, lk }) => {
  const f = state?.f || blankT;
  const set = (k) => (e) => setState({ ...state, f: { ...f, [k]: e.target.value } });
  const pick = (e) => {
    const t = lk.treatments.find((x) => x.id === e.target.value);
    setState({ ...state, f: { ...f, treatment_id: e.target.value, treatment_name: t?.name || "" } });
  };
  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && setState(null)}>
      <DialogContent className="bg-[#FDFBF7] border-[#E8E1D7] max-w-lg" data-testid="client-treatment-dialog">
        <DialogHeader><DialogTitle className="font-serif text-3xl font-normal">{state?.id ? "Modifica trattamento" : "Aggiungi trattamento"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Dal catalogo"><Select value={f.treatment_id} onChange={pick} placeholder="—" options={lk.treatments.map((t) => ({ value: t.id, label: t.name }))} data-testid="ct-catalog-select" /></Field>
          <Field label="Nome trattamento *"><input className={inputCls} value={f.treatment_name} onChange={set("treatment_name")} data-testid="ct-name-input" /></Field>
          <Field label="Operatore"><Select value={f.operator_id} onChange={set("operator_id")} placeholder="—" options={lk.operators.map((o) => ({ value: o.id, label: o.name }))} data-testid="ct-operator-select" /></Field>
          <Field label="Data"><input type="date" className={inputCls} value={f.date} onChange={set("date")} data-testid="ct-date-input" /></Field>
          <div className="sm:col-span-2"><Field label="Stato"><Select value={f.status} onChange={set("status")} options={[{ value: "da_effettuare", label: "Da effettuare" }, { value: "effettuato", label: "Effettuato" }]} data-testid="ct-status-select" /></Field></div>
          <div className="sm:col-span-2"><Field label="Note"><textarea rows={2} className={inputCls} value={f.notes} onChange={set("notes")} data-testid="ct-notes-input" /></Field></div>
        </div>
        <div className="flex justify-end"><GoldButton onClick={() => onSave(f)} data-testid="ct-save-button">Salva</GoldButton></div>
      </DialogContent>
    </Dialog>
  );
};

const TreatmentRow = ({ t, onEdit, onDone, onDelete }) => (
  <li className="flex flex-wrap items-center gap-3 py-4" data-testid={`ct-row-${t.id}`}>
    <div className="flex-1 min-w-[180px]">
      <div className="font-medium text-[#121212]">{t.treatment_name}</div>
      <div className="text-xs text-[#6E675F]">{fmtDate(t.date)} · {t.operator_name || "nessun operatore"}{t.notes ? ` · ${t.notes}` : ""}</div>
    </div>
    <StatusBadge status={t.status} />
    {t.status === "da_effettuare" && <button onClick={onDone} title="Segna come effettuato" className="text-[#2D6A4F] hover:opacity-70" data-testid={`ct-done-${t.id}`}><CheckCircle2 className="h-4 w-4" /></button>}
    <button onClick={onEdit} className="text-[#6E675F] hover:text-[#C5A059]" data-testid={`ct-edit-${t.id}`}><Pencil className="h-4 w-4" /></button>
    <button onClick={onDelete} className="text-[#6E675F] hover:text-red-700" data-testid={`ct-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
  </li>
);

export const TreatmentsTab = ({ clientId, lk }) => {
  const [items, load] = useList(`/clients/${clientId}/treatments`);
  const [dlg, setDlg] = useState(null);
  const save = async (f, id = dlg?.id) => {
    if (!f.treatment_name.trim()) return toast.error("Inserisci il nome del trattamento");
    const body = { treatment_id: f.treatment_id, treatment_name: f.treatment_name, operator_id: f.operator_id, date: f.date, status: f.status, notes: f.notes };
    try {
      if (id) await api.put(`/client-treatments/${id}`, body);
      else await api.post(`/clients/${clientId}/treatments`, body);
      toast.success("Trattamento salvato");
      setDlg(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { if (window.confirm("Eliminare il trattamento?")) { await api.delete(`/client-treatments/${id}`); load(); } };
  const todo = items?.filter((t) => t.status === "da_effettuare") || [];
  const done = items?.filter((t) => t.status === "effettuato") || [];
  const Section = ({ title, list, testId }) => (
    <Panel className="p-6" data-testid={testId}>
      <h3 className="font-serif text-2xl mb-2">{title} <span className="text-[#C5A059] text-lg">({list.length})</span></h3>
      {list.length === 0 ? <p className="py-4 text-sm text-[#9E958A]">Nessun trattamento.</p> : (
        <ul className="divide-y divide-[#E8E1D7]">
          {list.map((t) => <TreatmentRow key={t.id} t={t} onEdit={() => setDlg({ id: t.id, f: t })} onDelete={() => del(t.id)}
            onDone={() => save({ ...t, status: "effettuato", date: t.date || new Date().toISOString().slice(0, 10) }, t.id)} />)}
        </ul>
      )}
    </Panel>
  );
  return (
    <div>
      <div className="mb-4 flex justify-end"><GoldButton onClick={() => setDlg({ f: blankT })} data-testid="add-client-treatment-button"><Plus className="h-4 w-4" /> Aggiungi trattamento</GoldButton></div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Section title="Da effettuare" list={todo} testId="ct-todo-section" />
        <Section title="Effettuati" list={done} testId="ct-done-section" />
      </div>
      <TreatmentDialog state={dlg} setState={setDlg} onSave={save} lk={lk} />
    </div>
  );
};

export const AppointmentsTab = ({ clientId, lk }) => {
  const [items, load] = useList(`/appointments?client_id=${clientId}`);
  const [dlg, setDlg] = useState({ open: false });
  return (
    <div>
      <div className="mb-4 flex justify-end"><GoldButton onClick={() => setDlg({ open: true })} data-testid="add-client-appointment-button"><Plus className="h-4 w-4" /> Nuovo appuntamento</GoldButton></div>
      {items?.length === 0 && <Empty testId="client-appts-empty">Nessun appuntamento.</Empty>}
      {items?.length > 0 && (
        <Panel className="p-2">
          <ul className="divide-y divide-[#E8E1D7]">
            {items.slice().reverse().map((a) => (
              <li key={a.id} onClick={() => setDlg({ open: true, initial: a })} className="flex cursor-pointer items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 rounded-xl hover:bg-[#FDFBF7]" data-testid={`client-appt-${a.id}`}>
                <div className="w-20 sm:w-28 shrink-0 text-sm"><div className="font-medium">{fmtDate(a.start)}</div><div className="text-[#6E675F]">{a.start.slice(11, 16)}</div></div>
                <span className="h-8 w-1 rounded-full" style={{ background: a.operator_color }} />
                <div className="flex-1 min-w-0 text-sm"><div className="truncate">{a.treatment_name || "Trattamento"}</div><div className="truncate text-xs text-[#6E675F]">{a.operator_name || "—"}</div></div>
                <div className="flex shrink-0 flex-col items-end gap-1.5 sm:flex-row sm:items-center sm:gap-3">
                  {a.status === "programmato" && <WhatsAppButton appt={a} compact />}
                  <StatusBadge status={a.status} />
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      <AppointmentDialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })} initial={dlg.initial} fixedClientId={clientId} onSaved={load} clients={lk.clients} treatments={lk.treatments} operators={lk.operators} />
    </div>
  );
};

export const PurchasesTab = ({ clientId, lk }) => {
  const [items, load] = useList(`/clients/${clientId}/purchases`);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ product_id: "", quantity: 1, note: "" });
  const sel = lk.products.find((p) => p.id === f.product_id);
  const save = async () => {
    if (!f.product_id) return toast.error("Seleziona un prodotto");
    try {
      await api.post(`/clients/${clientId}/purchases`, { ...f, quantity: Number(f.quantity) });
      toast.success("Acquisto registrato, magazzino aggiornato");
      setOpen(false);
      setF({ product_id: "", quantity: 1, note: "" });
      load();
      lk.reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const total = (items || []).reduce((s, m) => s + m.quantity * (m.unit_price || 0), 0);
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-[#6E675F]">Totale speso: <span className="font-serif text-xl text-[#121212]" data-testid="purchases-total">{fmtEuro(total)}</span></div>
        <GoldButton onClick={() => setOpen(true)} data-testid="add-purchase-button"><Plus className="h-4 w-4" /> Registra acquisto</GoldButton>
      </div>
      {items?.length === 0 && <Empty testId="purchases-empty">Nessun prodotto acquistato.</Empty>}
      {items?.length > 0 && (
        <Panel className="p-2">
          <ul className="divide-y divide-[#E8E1D7]">
            {items.map((m) => (
              <li key={m.id} className="flex items-center gap-4 px-4 py-3" data-testid={`purchase-row-${m.id}`}>
                <ProductImage path={m.photo_path} className="h-12 w-12 rounded-xl" />
                <div className="flex-1 min-w-0"><div className="truncate font-medium">{m.product_name}</div><div className="text-xs text-[#6E675F]">{fmtDate(m.date)}{m.note ? ` · ${m.note}` : ""}</div></div>
                <div className="text-right text-sm"><div>{m.quantity} × {fmtEuro(m.unit_price)}</div><div className="font-medium">{fmtEuro(m.quantity * m.unit_price)}</div></div>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FDFBF7] border-[#E8E1D7] max-w-md" data-testid="purchase-dialog">
          <DialogHeader><DialogTitle className="font-serif text-3xl font-normal">Registra acquisto</DialogTitle></DialogHeader>
          <Field label="Prodotto"><Select value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} placeholder="Seleziona prodotto"
            options={lk.products.map((p) => ({ value: p.id, label: `${p.name} (${p.quantity} disp.)` }))} data-testid="purchase-product-select" /></Field>
          <Field label="Quantità"><input type="number" min="1" max={sel?.quantity} className={inputCls} value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="purchase-qty-input" /></Field>
          <Field label="Nota"><input className={inputCls} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="purchase-note-input" /></Field>
          <div className="flex justify-end gap-2"><GhostButton onClick={() => setOpen(false)}>Annulla</GhostButton><GoldButton onClick={save} data-testid="purchase-save-button">Conferma</GoldButton></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
